import { get, isArray, isEmpty, isUndefined } from "lodash";
import { PlotData, Layout, PlotType } from "plotly.js";
import { doHidden, COLORWAY } from "../utils";
import { IPieChart, IDisplay, IResult, IStructure } from "../types";
import { IOutputProps } from "./types";
import "./style.scss";
import { useMemo } from "react";
import ArchiveIcon from "./ArchiveIcon";
import { downloadData } from "./download";
import { ChartLoadingSkeleton } from "./loading";
import { isTemplateRefreshing } from "../dependencyIndex";
import RefreshingState from "../RefreshingState";
import AutoSizedPlot from "./AutoSizedPlot";

export interface IPieChartProps extends IOutputProps {
  template: IPieChart;
}

const renderLabel = (display: IDisplay) => {
  const label = display.label ? display.label : "";
  const info = display.info ? ` <span class="pb3-text-muted">${display.info}</span>` : "";
  return `${label}${info}`;
};

const getGridValues = (template: IPieChart) => {
  const count = template.figures.length;
  const columns = count % 2 === 0 ? 2 : 3;
  const rows = Math.ceil(count / columns);
  return { count, columns, rows };
};

const transformData = (template: IPieChart, result: IResult): Array<Partial<PlotData>> => {
  const { count, columns } = getGridValues(template);
  return template.figures
    .map(
      (f, i) =>
        ({
          type: "pie" as PlotType,
          name: renderLabel(f),
          hoverinfo: "all",
          ...(count > 1 && { domain: { row: Math.floor(i / columns), column: i % columns } }),
          ...(isArray(f.data)
            ? {
                values: f.data.map((d) => (isUndefined(d.value) ? get(result, d.name, []) : d.value)) as Array<number>,
                labels: f.data.map((d) => renderLabel(d)),
              }
            : (get(result, f.data.name, []) as Array<any>).reduce(
                (p, v) => {
                  p.values.push(get(v, (f.data as IStructure).value));
                  p.labels.push(get(v, (f.data as IStructure).label));
                  return p;
                },
                { values: [], labels: [] }
              )),
        } as Partial<PlotData>)
    )
    .filter((v) => !isEmpty(v.values) && !isEmpty(v.labels));
};

const createLayout = (template: IPieChart): Partial<Layout> => {
  const { count, rows, columns } = getGridValues(template);
  return {
    title: template.label,
    colorway: COLORWAY,
    ...(count > 1 && { grid: { rows: rows, columns: columns } }),
  };
};

const PieChart = (props: IPieChartProps) => {
  const { template, result, metadata } = props;
  const data = useMemo(() => transformData(template, result), [template, result]);
  const layout = useMemo(() => createLayout(template), [template]);
  const refreshing = isTemplateRefreshing(template, metadata, props.mutators.getPath);

  if (doHidden(props)) {
    return null;
  }

  if (isEmpty(data) && refreshing) {
    return (
      <ChartLoadingSkeleton
        className="chart-container"
        height={template.height}
        label={template.label || template.name}
        variant="plot"
      />
    );
  }

  if (isEmpty(data)) {
    return null;
  }

  const rows = get(layout, ["grid", "rows"], 1);
  const scale = template.height ? template.height : 200;
  return (
    <RefreshingState refreshing={refreshing}>
    <div className="chart-container" style={{ height: scale + rows * scale }}>
      <AutoSizedPlot
        chartId={template.name}
        data={data}
        layout={layout}
        config={{
          displaylogo: false,
          modeBarButtonsToAdd: [
            {
              name: "downloadData",
              title: "Download data as a csv",
              icon: ArchiveIcon,
              click: () => downloadData(template, data),
            },
          ],
        }}
      />
    </div>
    </RefreshingState>
  );
};

export default PieChart;
